import React from 'react';
import clsx from 'clsx';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { parsePercentage } from 'src/utils';
import { colors } from 'src/constants';
import { slices, useAppSelector } from 'src/redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { MetricCard } from 'src/components/card/MetricCard';

const { selectors: salesSelectors } = slices.sales;
interface Props {
  className?: string;
  isLoading?: boolean;
  increasedSalesVal?: string;
  isBasicDashboard?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  cardContent: {
    [theme.breakpoints.up('lg')]: {
      marginRight: '0rem',
      marginLeft: '0rem'
    },
    [theme.breakpoints.down('md')]: {
      paddingX: 0
    }
  },
  totalContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column'
  },
  gross: {
    color: colors.success
  }
}));

const SalesToolbar = ({
  className,
  isLoading = false,
  increasedSalesVal,
  isBasicDashboard = false,
  ...rest
}: Props) => {
  const classes = useStyles();

  const salesComputation = useAppSelector(
    salesSelectors.selectSalesResponseComputation
  );

  const salesComputationLastMonth = useAppSelector(
    salesSelectors.selectSalesComputationLastMonth
  );

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box
        mt={3}
        className={classes.cardContent}
        style={{
          position: 'relative',
          minHeight: '300px'
        }}
      >
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgcolor="rgba(255, 255, 255, 0.7)"
            style={{ borderRadius: '8px', zIndex: 2 }}
          >
            <CircularProgress size={80} />
          </Box>
        )}

        <Grid
          container
          spacing={3}
          alignItems="center"
          justify="center"
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          {isBasicDashboard ? (
            <>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Balance Amount'}
                    currentValue={salesComputation?.total_balance}
                    previousValue={salesComputationLastMonth?.total_balance}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Paid Amount'}
                    currentValue={salesComputation?.total_paid}
                    previousValue={salesComputationLastMonth?.total_paid}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'No. of Transactions'}
                    currentValue={salesComputation?.transactions}
                    previousValue={salesComputationLastMonth?.transactions}
                    isCount
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Retail Sales'}
                    currentValue={salesComputation?.total_retail_price}
                    previousValue={
                      salesComputationLastMonth?.total_retail_price
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Voucher Amount'}
                    currentValue={
                      salesComputation?.total_voucher_discount_amount
                    }
                    previousValue={
                      salesComputationLastMonth?.total_voucher_discount_amount
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div
                  className={classes.totalContainer}
                  style={{ textAlign: 'center' }}
                >
                  <MetricCard
                    title={'Redeemed Kachi Points'}
                    currentValue={salesComputation?.total_spend_kachi_points}
                    previousValue={
                      salesComputationLastMonth?.total_spend_kachi_points
                    }
                  />
                </div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Retail Sales'}
                    currentValue={salesComputation?.total_retail_price}
                    previousValue={
                      salesComputationLastMonth?.total_retail_price
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Dealer Sales'}
                    currentValue={salesComputation?.total_dealers_price}
                    previousValue={
                      salesComputationLastMonth?.total_dealers_price
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Gross Sales'}
                    currentValue={salesComputation?.total_gross}
                    previousValue={salesComputationLastMonth?.total_gross}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Paid Amount'}
                    currentValue={salesComputation?.total_paid}
                    previousValue={salesComputationLastMonth?.total_paid}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Balance Amount'}
                    currentValue={salesComputation?.total_balance}
                    previousValue={salesComputationLastMonth?.total_balance}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Margin Percentage'}
                    currentValue={parsePercentage(
                      salesComputation?.margin_percentage
                    )}
                    previousValue={parsePercentage(
                      salesComputationLastMonth?.margin_percentage
                    )}
                    isPercentage
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div
                  className={classes.totalContainer}
                  style={{ textAlign: 'center' }}
                >
                  <MetricCard
                    title={'Redeemed Kachi Points'}
                    currentValue={salesComputation?.total_spend_kachi_points}
                    previousValue={
                      salesComputationLastMonth?.total_spend_kachi_points
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Voucher Amount'}
                    currentValue={
                      salesComputation?.total_voucher_discount_amount
                    }
                    previousValue={
                      salesComputationLastMonth?.total_voucher_discount_amount
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Total Net Sales'}
                    currentValue={
                      (salesComputation?.total_gross || 0) -
                      (salesComputation?.total_voucher_discount_amount || 0)
                    }
                    previousValue={
                      (salesComputationLastMonth?.total_gross || 0) -
                      (salesComputationLastMonth?.total_voucher_discount_amount ||
                        0)
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'Margin Percentage With Voucher'}
                    currentValue={parsePercentage(
                      salesComputation?.margin_percentage_with_voucher
                    )}
                    previousValue={parsePercentage(
                      salesComputationLastMonth?.margin_percentage_with_voucher
                    )}
                    isPercentage
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <div className={classes.totalContainer}>
                  <MetricCard
                    title={'No. of Transactions'}
                    currentValue={salesComputation?.transactions}
                    previousValue={salesComputationLastMonth?.transactions}
                    isCount
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <MetricCard
                  title={'Increased Sales from Last Month'}
                  currentValue={parseFloat(increasedSalesVal ?? '0')}
                  isPercentage
                  isGrowth
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </div>
  );
};

export default SalesToolbar;
